import { Module } from 'vuex'
import {
  getDeviceFlyLogs,
  getServiceOrderDetailApplyInfo,
  getFlyLogBoomInfo,
} from '@/apis/malfunction-repair'
import {
  GetDeviceFlyLogsItem,
  StoreFlyLogsDataSnMap,
  NetworkListRenderItem,
  MalfunctionRepairFormFirstData,
  MalfunctionRepairCertificateListItem,
  MalfunctionRepairServiceOrderApplyInfo,
  MalfunctionRepairServiceOrderLogisticsItem,
  FlylogAnalysisResult,
} from '@/types/malfunction-repair'
import { commonErrorHandle } from '@/utils/error-handle'

export type MalfunctionRepairStateApplyInfoItem = {
  loading: boolean
  data: Promise<MalfunctionRepairServiceOrderApplyInfo>
  status?: string // failed代表失败
}
export interface MalfunctionRepairState {
  selectedLogGuid: string
  selectedLogItem: GetDeviceFlyLogsItem
  hasUseCache: boolean
  useCacheId: string // 缓存设备id, 用于提单成功时删除
  //flyLogs: GetDeviceFlyLogsItem[]
  flylogsSnMap: StoreFlyLogsDataSnMap
  flylogLoading: boolean
  isBoom: boolean | undefined
  networkInfo: null | NetworkListRenderItem
  keepAliveIncludes: string[] // 缓存的页面
  firstData: MalfunctionRepairFormFirstData // 表单第一步数据
  // 购买凭证数据
  certificateDataSnMap: Record<string, MalfunctionRepairCertificateListItem>
  // 服务单提单信息
  orderApplyInfoGuidMap: Record<string, MalfunctionRepairStateApplyInfoItem>
  // 服务单详情
  orderDetailLogistics: MalfunctionRepairServiceOrderLogisticsItem | null
  // 图片guid和完整url映射
  imageGuidUrlMap: Record<string, string>
  submitOrderSuccessGuid: string
  // 分析结果数据
  analysisInfo: FlylogAnalysisResult | null
}
const faultFormName = 'malfunction-repair-fault-form'
const networkName = 'malfunction-repair-network-list'
const serviceDetailName = 'malfunction-repair-service-order-detail'
const repairDetailName = 'malfunction-repair-repair-order-detail'
const dutyResultName = 'malfunction-repair-repair-order-duty-result'
const faultOrderListName = 'malfunction-repair-order-list'
const updateMaintainDetailName = 'malfunction-upgrade-maintenance-detail'
function initFirstData(): MalfunctionRepairFormFirstData {
  // 初始化部分数据
  return {
    description: '',
    files: [],
    annextList:[],
    // 报修类型
    is_pay: 0,
    // receive_express: null,
  }
}
function getOrderApplyInfoWrap(guid: string) {
  const wrapFn = getServiceOrderDetailApplyInfo(guid)
  return wrapFn
}
function addKeepAliveItem(state: MalfunctionRepairState, pageName: string) {
  if (state.keepAliveIncludes.indexOf(pageName) !== -1) {
    return
  }
  state.keepAliveIncludes = state.keepAliveIncludes.concat(pageName)
}
function removeKeepAliveItem(state: MalfunctionRepairState, pageName: string) {
  state.keepAliveIncludes = state.keepAliveIncludes.filter((item) => item !== pageName)
}
const MalfunctionRepair: Module<MalfunctionRepairState, any> = {
  namespaced: true,
  state: function () {
    return {
      selectedLogGuid: '',
      isBoom: undefined,
      hasUseCache: true, // 判断是否已经使用缓存，针对提单时跳转其他页面又返回时
      useCacheId: '',
      flylogsSnMap: {},
      flylogLoading: false,
      // 已选择飞行日志
      selectedLogItem: null,
      // 网点数据
      networkInfo: null,
      // 第一步数据
      firstData: initFirstData(),
      keepAliveIncludes: ['malfunction-repair-devices', faultOrderListName],
      certificateDataSnMap: {},
      orderApplyInfoGuidMap: {},
      orderDetailLogistics: null,
      imageGuidUrlMap: {},
      // 存储提单成功后的guid， 理论上使用一次后清空
      submitOrderSuccessGuid: '',
      analysisInfo: null,
    }
  },
  mutations: {
    setSelectedLogGuid(state, value: string): void {
      state.selectedLogGuid = value
    },
    setFlylogsSnMap(
      state,
      option: {
        sn: string
        logs: GetDeviceFlyLogsItem[]
      }
    ) {
      state.flylogsSnMap[option.sn] = option.logs
    },
    setBoomStatus(state, isBoom: boolean) {
      state.isBoom = isBoom
    },
    addFaultFormKeepAlive(state) {
      addKeepAliveItem(state, faultFormName)
    },
    removeFaultFormKeepAlive(state) {
      removeKeepAliveItem(state, faultFormName)
    },
    addNetworkListKeepAlive(state) {
      addKeepAliveItem(state, networkName)
    },
    setSelectedLogItem(state, item: GetDeviceFlyLogsItem) {
      state.selectedLogItem = item
    },
    removeNetworkListKeepAlive(state) {
      removeKeepAliveItem(state, networkName)
    },
    addServiceOrderKeepAlive(state) {
      addKeepAliveItem(state, serviceDetailName)
    },
    removeServiceOrderKeepAlive(state) {
      removeKeepAliveItem(state, serviceDetailName)
    },
    addOrderRepairDetailItemKeepAlive(state) {
      addKeepAliveItem(state, repairDetailName)
    },
    removeOrderRepairDetailItemKeepAlive(state) {
      removeKeepAliveItem(state, repairDetailName)
    },
    addOrderDutyResultItemKeepAlive(state) {
      addKeepAliveItem(state, dutyResultName)
    },
    removeOrderDutyResultItemKeepAlive(state) {
      removeKeepAliveItem(state, dutyResultName)
    },
    addFaultOrderListItemKeepAlive(state) {
      addKeepAliveItem(state, faultOrderListName)
    },
    removeFaultOrderListItemKeepAlive(state) {
      removeKeepAliveItem(state, faultOrderListName)
    },
    setNetworkInfo(state, item: NetworkListRenderItem) {
      state.networkInfo = item
    },
    setFormFirstData(state, item: MalfunctionRepairFormFirstData) {
      state.firstData = { ...item }
    },
    setCertificateData(
      state,
      data: {
        sn: string
        item: MalfunctionRepairCertificateListItem
      }
    ) {
      state.certificateDataSnMap[data.sn] = {
        ...state.certificateDataSnMap[data.sn],
        ...data.item,
      }
    },
    setOrderDetailAddressInfo(state, item: MalfunctionRepairServiceOrderLogisticsItem) {
      state.orderDetailLogistics = {
        ...item,
      }
    },
    clearFormData(state) {
      // 清除提单数据
      state.selectedLogGuid = ''
      state.isBoom = undefined
      state.flylogsSnMap = {} // 日志在提单后需要的清除，方便后续用到最新得数据
      state.flylogLoading = false
      // 重置已使用缓存
      state.hasUseCache = false
      state.useCacheId = ''
      // 已选择飞行日志
      state.selectedLogItem = null
      // 网点数据
      state.networkInfo = null
      // 凭证数据
      state.certificateDataSnMap = {}
      // 清楚日志数据
      state.analysisInfo = null
      // 第一步数据
      state.firstData = initFirstData()
    },
    addUpdateMaintainOrderKeepAlive(state) {
      addKeepAliveItem(state, updateMaintainDetailName)
    },
    removeUpdateMaintainOrderKeepAlive(state) {
      removeKeepAliveItem(state, updateMaintainDetailName)
    },
  },
  actions: {
    async initFlylogs({ commit, state }, sn: string) {
      state.flylogLoading = true
      try {
        const data = await getDeviceFlyLogs({
          serial_number: sn,
          page: 1,
          per_page: 5,
        })
        // 异步加载各个炸机信息
        const boomInfoList = await Promise.all(
          data.data.map((item) => getFlyLogBoomInfo(item.guid))
        )
        const logs = data.data.map((item, index) => ({
          ...item,
          isBoom: boomInfoList[index].isBoom,
        }))
        commit('setFlylogsSnMap', { sn, logs })
        state.flylogLoading = false
        return logs
      } catch (error) {
        state.flylogLoading = false
        console.log('error', error)
        return Promise.reject(error)
      }
    },
    async getOrderApplyInfo({ commit, state }, guid: string) {
      const wrapFn = getOrderApplyInfoWrap(guid)
      const current: MalfunctionRepairStateApplyInfoItem = {
        loading: true,
        data: wrapFn,
      }
      state.orderApplyInfoGuidMap[guid] = current
      current.loading = true
      let data
      try {
        data = await wrapFn
      } catch (error) {
        commonErrorHandle(error)
        current.status = 'failed'
      }
      current.loading = false
      return data
    },
  },
}
export default MalfunctionRepair
